import { Box, LinearProgress, Paper, Typography } from "@mui/material";
import { useCallback, useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router";
import { AdditionalServicesModal } from "../../modules/additional-services/additional-services-modal";
import type { AdditionalServicesType } from "../../modules/additional-services/type";
import { ClickwrapModal } from "../../modules/clickwrap/clickwrap-modal";
import {
  useClickwrapTerms,
  type ClickwrapFetchStatus,
} from "../../modules/clickwrap/use-clickwrap-terms";
import { HeaderBar } from "../../modules/generic/components/header-bar";
import { Page } from "../../modules/layout/components/page";
import { useFlags } from "../../server/optimizely";
import { useDialog } from "../../utils/hooks/use-dialog";
import NotFound from "../not-found";

type ClickwrapModalStatus = Exclude<ClickwrapFetchStatus, "idle">;

const ExosPage: FC = () => {
  const {
    flags: { ["cha-4488_exos"]: exos },
    flagsLoaded,
  } = useFlags("cha-4488_exos");
  const dialog = useDialog<{ service: AdditionalServicesType }>(true, { service: "exos" });
  const {
    title: title,
    status: clickwrapStatus,
    contentHtml: clickwrapContentHtml,
    contentFormat: clickwrapContentFormat,
    errorMessage: clickwrapErrorMessage,
    hasAcceptedCurrentVersion,
    fetchClickwrapTerms,
    acceptClickwrapTerms,
  } = useClickwrapTerms("exos");
  const [hasDismissedClickwrap, setHasDismissedClickwrap] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasDismissedClickwrap || clickwrapStatus !== "idle") return;
    void fetchClickwrapTerms();
  }, [clickwrapStatus, fetchClickwrapTerms, hasDismissedClickwrap]);

  const onAgreeClickwrap = useCallback(() => {
    void acceptClickwrapTerms()
      .then(() => {
        setHasDismissedClickwrap(true);
      })
      .catch((err) => {
        console.error("Failed to persist clickwrap acceptance", err);
      });
  }, [acceptClickwrapTerms]);
  const onCancelClickwrap = useCallback(() => {
    void navigate("/");
  }, [navigate]);

  const onRetryClickwrap = useCallback(() => {
    void fetchClickwrapTerms();
  }, [fetchClickwrapTerms]);

  if (!flagsLoaded) return <LinearProgress />;
  if (!exos) return <NotFound />;

  const isEvaluatingClickwrapGate =
    !hasDismissedClickwrap && clickwrapStatus !== "loaded" && clickwrapStatus !== "error";
  const showClickwrap =
    !hasDismissedClickwrap &&
    !hasAcceptedCurrentVersion &&
    (clickwrapStatus === "loaded" || clickwrapStatus === "error");
  const modalStatus: ClickwrapModalStatus =
    clickwrapStatus === "idle" ? "loading" : clickwrapStatus;

  if (isEvaluatingClickwrapGate) return <LinearProgress />;
  return (
    <Page title="EXOS | ChangeEngine">
      <HeaderBar navBurger>
        <Typography variant="body1" fontWeight={700} fontSize={16}>
          EXOS
        </Typography>
      </HeaderBar>
      <Box
        component="main"
        sx={{
          height: "100vh",
          width: "100%",
          overflow: "hidden",
        }}
      >
        {showClickwrap ? (
          <ClickwrapModal
            open={showClickwrap}
            title={title}
            onCancel={onCancelClickwrap}
            onAgree={onAgreeClickwrap}
            status={modalStatus}
            contentHtml={clickwrapContentHtml}
            contentFormat={clickwrapContentFormat}
            errorMessage={clickwrapErrorMessage ?? undefined}
            onRetry={onRetryClickwrap}
          />
        ) : (
          <>
            <Paper sx={{ width: "50%", margin: "auto", p: 10 }}>Comming soon...</Paper>
            <AdditionalServicesModal dialog={dialog} />
          </>
        )}
      </Box>
    </Page>
  );
};

export default ExosPage;
